import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  type AutocompleteProps,
} from "@mui/material";
import { useMemo, type ReactNode, type SyntheticEvent } from "react";
import { themeColors } from "../../app/layout/theme";

export type SelectOption = string | { label: string; value: string | number };

function getOptionLabel(opt: SelectOption): string {
  return typeof opt === "string" ? opt : opt.label;
}

function optionsEqual(a: SelectOption, b: SelectOption): boolean {
  if (typeof a === "string" && typeof b === "string") return a === b;
  if (typeof a !== "string" && typeof b !== "string")
    return a.value === b.value;
  return false;
}

export interface FormSelectProps<T extends SelectOption = string> {
  label?: string;

  labelPosition?: "above" | "floating";

  required?: boolean;
  options: readonly T[];
  value?: T | string | number | (T | string | number)[] | null;
  onChange?: (value: any) => void;

  fallbackValue?: T | null;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  freeSolo?: boolean;
  onInputChange?: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  size?: "small" | "medium";
  noOptionsText?: string;
  endAdornment?: ReactNode;
  sx?: object;
  inputSx?: object;
  fullWidth?: boolean;
  disableClearable?: boolean;
}

export function FormSelect<T extends SelectOption = string>({
  label,
  labelPosition = "floating",
  required,
  options,
  value,
  onChange,
  fallbackValue = null,
  error,
  helperText,
  placeholder,
  freeSolo = false,
  onInputChange,
  loading = false,
  disabled,
  multiple = false,
  size = "small",
  noOptionsText,
  endAdornment,
  sx,
  inputSx,
  fullWidth = true,
  disableClearable,
}: FormSelectProps<T> & { fullWidth?: boolean }) {
  const isAbove = labelPosition === "above";

  const selectedOption = useMemo(() => {
    if (multiple) {
      if (!Array.isArray(value)) return [];
      return value.map(v => typeof v === 'object' ? v : options.find(o => typeof o !== 'string' && (o as any).value === v) || v);
    }
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') return value;
    return options.find(o => typeof o !== 'string' && (o as any).value === value) || value;
  }, [value, options, multiple]);

  const handleChange = (
    _: SyntheticEvent,
    newValue: T | T[] | string | null,
  ) => {
    if (!onChange) return;
    if (multiple) {
      onChange(newValue as T[]);
      return;
    }
    onChange((newValue as T | null) ?? fallbackValue);
  };
  const autocompleteProps = {
    value: selectedOption,
    options: options as T[],
    getOptionLabel: (opt: T | string) =>
      typeof opt === "string" ? opt : getOptionLabel(opt as T),
    isOptionEqualToValue: (opt: T, val: T) => optionsEqual(opt, val),
    onChange: handleChange,
    onInputChange: freeSolo
      ? (_: SyntheticEvent, v: string) => onInputChange?.(v)
      : undefined,
    freeSolo,
    multiple,
    disabled,
    loading,
    noOptionsText,
    fullWidth,
    disableClearable,
  } as unknown as AutocompleteProps<T, boolean, false, boolean>;

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {isAbove && label && (
        <Typography
          component="label"
          sx={{
            display: "block",
            fontSize: "12px",
            fontWeight: 600,
            color: error ? themeColors.error : themeColors.text.primary,
            mb: 0.5,
            userSelect: "none",
            lineHeight: 1.4,
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: themeColors.error, ml: "2px" }}>
              *
            </Box>
          )}
        </Typography>
      )}

      <Autocomplete
        {...autocompleteProps}
        renderInput={(params) => (
          <TextField
            {...params}
            label={isAbove ? undefined : label}
            required={!isAbove && required}
            placeholder={placeholder}
            size={size}
            error={error}
            helperText={helperText}
            sx={inputSx}
            fullWidth={fullWidth}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                  {endAdornment}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
